import { Game } from '../../../../features/game/types/Game'
import { Quarter } from '../../../../features/quarter/types/Quarter'
import { TopGame } from '../../../../features/top-game/types/TopGame'
import { Analyst } from '../../../../types/Analyst'
import { GameAnalystMap } from '../../games/types/InternalGame'
import { DashboardGameRow, DashboardVersion, StoredSubgenres, TopGrossingHistoryEntry, TopGrossingRankedGame, allAnalystsId } from '../types/Dashboard'

// Legacy's lists without yesterday's ranks, which the dashboard doesn't show; `gpsPerMarket` twice included.
export const dashboardGameFields = [
  'appId',
  'name',
  'artist',
  'icon',
  'icons',
  'gpsPerMarket',
  'updatedAt',
  'ranks',
  'lastAnalysisAt',
  'gpsPerMarket',
  'genreId',
  'subGenreId',
]
export const outdatedGameFields = [...dashboardGameFields, 'conventionalSubgenre']
export const topGameFields = [
  'name',
  'artist',
  'images',
  'gps',
  'lastAnalysisAt',
  'rank',
  'drank',
  'gameId',
  'conventionalSubgenre',
  'conventionalSubgenreId',
  'appId',
  'reviewId',
  'reviewPublished',
]
// Legacy's list plus `gameId`, which the game dialogs need.
export const marketShareGameFields = ['appId', 'name', 'artist', 'images', 'conventionalSubgenreId', 'conventionalSubgenre', 'archive', 'gameId']
export const pinnedGameFields = [
  'appId',
  'name',
  'artist',
  'icon',
  'icons',
  'gpsPerMarket',
  'updatedAt',
  'ranks',
  'lastAnalysisAt',
  'conventionalSubgenre',
  'conventionalSubgenreId',
  'genreId',
  'subGenreId',
  'reviewId',
  'reviewPublished',
]

export const topGamesLimit = 200
export const defaultMarketIso = 'us'
const top200Days = 90
const dayMs = 24 * 60 * 60 * 1000

export const getInitialMarketIso = (savedIso: string | undefined, publishedIsos: string[]) => {
  if (savedIso && publishedIsos.includes(savedIso)) return savedIso
  return publishedIsos.includes(defaultMarketIso) ? defaultMarketIso : publishedIsos[0]
}

export const roundPowerscore = (powerscore?: number | null) => (powerscore ? Math.round(powerscore) : undefined)

// `Game` reads the subgenre from the language maps by id; the answer's own name is kept when that finds none.
export const toDashboardGame = (data: Record<string, unknown>) => {
  const game = new Game(data as unknown as Game)
  if (!game.conventionalSubgenre && typeof data.conventionalSubgenre === 'string') game.conventionalSubgenre = data.conventionalSubgenre
  return game
}

export const toTopGameRow = (top: TopGame): DashboardGameRow => ({
  key: top.id || top.gameId || `app-${top.appId}`,
  game: toDashboardGame({
    id: top.gameId ?? '',
    appId: top.appId,
    name: top.name,
    artist: top.artist,
    icons: top.images ?? [],
    conventionalSubgenreId: top.conventionalSubgenreId,
    conventionalSubgenre: top.conventionalSubgenre,
    reviewId: top.reviewId,
    reviewPublished: top.reviewPublished,
  }),
  rank: top.rank,
  freeRank: top.drank,
  powerscore: roundPowerscore(top.gps),
})

export const toGameRow = (game: Game, marketIso: string): DashboardGameRow => ({
  key: game.id,
  game,
  rank: game.ranks?.[marketIso],
  powerscore: roundPowerscore(game.gpsPerMarket?.[marketIso]),
})

export const countDaysInTop200 = (history: TopGrossingHistoryEntry[] = [], now = Date.now()) =>
  history.filter(({ ts, rank }) => ts > now - top200Days * dayMs && rank < 201).length

export const toNotAnalyzedRows = (games: Game[], ranked: Record<string, TopGrossingRankedGame>, marketIso: string, now = Date.now()): DashboardGameRow[] =>
  games.map((game) => {
    const top = game.appId ? ranked[String(game.appId)] : undefined
    return { ...toGameRow(game, marketIso), rank: top?.rank, daysInTop200: countDaysInTop200(top?.history, now) }
  })

export const toOutdatedRows = (games: Game[], versions: Record<string, DashboardVersion>, marketIso: string): DashboardGameRow[] =>
  games.map((game) => ({ ...toGameRow(game, marketIso), firstUnanalyzedVersion: game.appId ? versions[String(game.appId)] : undefined }))

export const getAppIdsWithGames = (games: Game[]) => Array.from(new Set(games.map(({ appId }) => appId).filter((appId) => !!appId)))

export const getMarketShareAppIds = (quarters: Quarter[]) =>
  Array.from(new Set(quarters.flatMap(({ grossingGames, freeGames }) => [...(grossingGames ?? []), ...(freeGames ?? [])])))

// Legacy keeps `archive != 0`: the games with `archive: true`, or none.
export const isMarketShareGameMissingSubgenre = (game: TopGame) => {
  const archive = game.archive as unknown
  return !game.conventionalSubgenreId && archive !== false && archive !== 0
}

export const getAnalystsByAppId = (analystMap: GameAnalystMap, analysts: Analyst[]) => {
  const byId = new Map(analysts.map((analyst) => [analyst.id, analyst]))
  return Object.entries(analystMap).reduce<Record<string, Analyst>>((result, [appId, analystId]) => {
    const analyst = byId.get(analystId)
    if (analyst) result[appId] = analyst
    return result
  }, {})
}

export const sortAnalystsByName = (analysts: Analyst[]) => [...analysts].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))

export const filterRowsByAnalyst = (rows: DashboardGameRow[], analystMap: GameAnalystMap, analystId: string) =>
  analystId === allAnalystsId ? rows : rows.filter(({ game }) => !!game.appId && analystMap[String(game.appId)] === analystId)

export const filterRowsByName = (rows: DashboardGameRow[], term: string) => {
  const search = term.trim().toLowerCase()
  return search ? rows.filter(({ game }) => (game.name ?? '').toLowerCase().includes(search)) : rows
}

export const getTopListHeadingKey = (count: number) => {
  if (count > 500) return 'internal-analysts:dashboard_top_1000'
  if (count > 200) return 'internal-analysts:dashboard_top_500'
  return 'internal-analysts:dashboard_top_200'
}

export const getRowSubgenre = (row: DashboardGameRow, storedSubgenres: StoredSubgenres) =>
  row.game.id && row.game.id in storedSubgenres ? storedSubgenres[row.game.id] ?? '' : row.game.conventionalSubgenre ?? ''

export const removeRowsWithStoredSubgenre = (rows: DashboardGameRow[], storedSubgenres: StoredSubgenres) =>
  rows.filter(({ game }) => !(game.id && storedSubgenres[game.id]))

export const formatReleaseDate = (timestamp?: number) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
}
